import {
  ChevronFirst,
  ChevronLast,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';
import { cn } from '../../lib/utils';
import useI18n from '../../hooks/useI18n';
import { getPageRange } from './utils';

export type PaginatorProps = {
  /** Current page, 1-based. */
  page: number;
  /** Total number of pages. Renders nothing when 1 or less. */
  pageCount: number;
  /** Called with the new 1-based page number. */
  onPageChange: (page: number) => void;
  /** Pages shown on each side of the current page. */
  siblingCount?: number;
  className?: string;
};

export function Paginator({
  page,
  pageCount,
  onPageChange,
  siblingCount = 3,
  className,
}: PaginatorProps) {
  const { t } = useI18n();

  if (pageCount <= 1) return <div />;

  const windowSize = siblingCount + 1 + siblingCount;
  const showEdgeButtons = pageCount > windowSize + 1;

  return (
    <div
      className={cn(
        'eye:flex eye:items-center eye:justify-center eye:gap-x-4xs',
        className,
      )}
    >
      {showEdgeButtons && (
        <Tooltip content={t('firstPage')}>
          <Button
            variant='ghost'
            size='icon'
            aria-label={t('firstPage')}
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
          >
            <ChevronFirst aria-hidden />
          </Button>
        </Tooltip>
      )}
      <Tooltip content={t('previousPage')}>
        <Button
          variant='ghost'
          size='icon'
          aria-label={t('previousPage')}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeftIcon aria-hidden />
        </Button>
      </Tooltip>

      {getPageRange(page, pageCount, siblingCount).map((pageNumber) => (
        <Button
          key={pageNumber}
          variant={pageNumber === page ? 'black' : 'ghost'}
          size='icon'
          aria-label={t('goToPage', { page: pageNumber })}
          aria-current={pageNumber === page ? 'page' : undefined}
          onClick={() => onPageChange(pageNumber)}
        >
          {pageNumber}
        </Button>
      ))}

      <Tooltip content={t('nextPage')}>
        <Button
          variant='ghost'
          size='icon'
          aria-label={t('nextPage')}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
        >
          <ChevronRightIcon aria-hidden />
        </Button>
      </Tooltip>

      {showEdgeButtons && (
        <Tooltip content={t('lastPage')}>
          <Button
            variant='ghost'
            size='icon'
            aria-label={t('lastPage')}
            onClick={() => onPageChange(pageCount)}
            disabled={page >= pageCount}
          >
            <ChevronLast aria-hidden />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}
